import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ChartDataset, ChartEvent, ChartOptions, ScriptableContext } from 'chart.js';
import { ActiveElement } from 'chart.js/dist/plugins/plugin.tooltip';
import { DEFAULT_PERSON_SELECTIONS, RACE_LABELS } from 'app/constants/person.constants';
import { IChartSelections } from 'app/models/chart.models';
import { IGenerateRaceDataFunc, Races } from 'app/models/person.models';
import { getNewChartFiltersByLabel } from 'app/utils/chart.utils';
import { Base64Image, exportChartToBase64Image } from 'app/utils/pdf.utils';
import { BaseChartDirective, NgChartsModule } from 'ng2-charts';

@Component({
  selector: 'app-by-race-chart',
  templateUrl: './by-race-chart.component.html',
  styleUrls: ['./by-race-chart.component.scss'],
  imports: [NgChartsModule],
})
export class ByRaceChartComponent implements AfterViewInit {
  @ViewChild(BaseChartDirective)
  chart!: BaseChartDirective;

  private internalGenerateData: IGenerateRaceDataFunc = () => {
    return { total: [], filtered: [], labels: [] };
  };

  @Input() set generateData(value: IGenerateRaceDataFunc) {
    this.internalGenerateData = value;
  }

  get generateData(): IGenerateRaceDataFunc {
    return this.internalGenerateData;
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  private internalSelections: IChartSelections = DEFAULT_PERSON_SELECTIONS;

  @Input() set selections(value: IChartSelections) {
    this.internalSelections = value;
    this.updateChartData();
  }

  get selections() {
    return this.internalSelections;
  }

  @Output() setSelections: EventEmitter<{
    newFilters: string[];
    resetOther: boolean;
  }> = new EventEmitter();

  @Input() name = '';
  private filteredData: { label: Races; value: number }[] = [];
  private totalData: { label: Races; value: number }[] = [];

  chartData: ChartDataset<'bar', number[]>[] = [
    {
      data: [],
      backgroundColor: (context: ScriptableContext<'bar'>) => {
        const color = '#118DFF';

        if (context.dataIndex === undefined || context.dataIndex < 0) return color;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const element = (context as any).element; // there is no such in TS, but it really exists;
        if (!element?.x || !element?.width) return color;

        const { ctx } = context.chart;

        let percent = 1;
        if (
          this.selections[this.name].length > 0 &&
          !this.selections[this.name].includes(this.totalData[context.dataIndex].label)
        ) {
          percent = 0;
        } else if (this.totalData[context.dataIndex].value > 0) {
          percent = this.filteredData[context.dataIndex].value / this.totalData[context.dataIndex].value;
        }

        if (percent !== 1) {
          const gradient = ctx.createLinearGradient(0, element.y + element.height, 0, element.y);

          gradient.addColorStop(0, color);
          gradient.addColorStop(percent, color);
          gradient.addColorStop(percent, color + '88');
          gradient.addColorStop(1, color + '88');

          return gradient;
        }

        return color;
      },
      borderWidth: 0,
    },
  ];

  chartOptions: ChartOptions<'bar'>;

  chartLabels: string[] = [];

  maxValue = 0;

  constructor() {
    // we need it to have access to the class this in the tooltip footer function
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const thisObj = this;

    this.chartOptions = {
      responsive: true,
      plugins: {
        title: {
          display: false,
        },
        legend: {
          display: false,
          position: 'right',
          align: 'start',
          labels: {
            usePointStyle: true,
          },
          title: {
            text: 'Race/Ethnicity',
            display: true,
            font: {
              size: 14,
            },
          },
        },
        tooltip: {
          displayColors: false,
          enabled: true,
          callbacks: {
            title: (tooltipItem) => {
              return `Race: ${tooltipItem[0].label}`;
            },
            label: (tooltipItem) => {
              const { value } = this.filteredData[tooltipItem.dataIndex];
              return `Count of Citations: ${value}`;
            },
            footer: function () {
              const percent = thisObj.totalData[this.dataPoints[0].dataIndex].value / thisObj.maxValue;
              const lines = [`Percent of first: ${Number(percent * 100).toFixed(2)}%`];

              if (this.dataPoints[0].dataIndex > 0) {
                const previousPercent =
                  thisObj.totalData[this.dataPoints[0].dataIndex].value /
                  thisObj.totalData[this.dataPoints[0].dataIndex - 1].value;
                lines.push(`Percent of previous: ${Number(previousPercent * 100).toFixed(2)}%`);
              }

              return lines;
            },
          },
        },
      },
      scales: {
        x: {
          display: true,
          grid: {
            display: false,
          },
        },
        y: {
          stacked: true,
        },
      },
      animation: {
        duration: 300,
      },
      maintainAspectRatio: false,
    };
  }

  ngAfterViewInit(): void {
    this.updateChartData();
  }

  private updateChartData() {
    const { total, filtered, labels } = this.generateData();
    const values = total.map(({ value }) => value);

    this.chartLabels = labels.map((label) => RACE_LABELS[label]);
    this.chartData[0].data = values;
    this.maxValue = Math.max(...values);

    this.totalData = total;
    this.filteredData = filtered;
  }

  // disable rule because this type came from library
  // eslint-disable-next-line @typescript-eslint/ban-types
  onChartClick = (e: { event?: ChartEvent; active?: {}[] }) => {
    const element = e?.active?.[0] as ActiveElement | undefined;
    if (!e.event || !element) return;

    const newFilters = getNewChartFiltersByLabel(
      this.selections[this.name],
      e.event,
      this.totalData[element.index].label,
    );

    if (newFilters.anyChanges && this.setSelections) {
      this.setSelections.emit({
        newFilters: newFilters.newSelectionFilter,
        resetOther: newFilters.resetOtherSelectionFilters,
      });
    }
  };

  exportToImage(): Base64Image | null {
    return exportChartToBase64Image(this.chart);
  }
}
